import React, { useEffect, useState, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button, Select } from 'antd';
import moment from 'moment';
import { GmapDivStyled, DivStyled } from './styled';
import isochrone from './isochrone';
import { httpGetAsync } from '$app/utils/util';

const { Option } = Select;

let map = '';
let polygon = '';
let deleteMenu = '';
let center = { lat: 0.0, lng: 0.9 };

const coordsArrToLatLngArrObjects = ([lat, lng]) => new google.maps.LatLng(lng, lat);

const initMap = (garage, otherGaragesCoverage) => {
  const { lat, lng, coverage } = garage;
  const { geoJson } = coverage || {};
  center = { lat, lng };

  map = new window.google.maps.Map(document.getElementById('map'), {
    center,
    zoom: 12,
    mapTypeId: 'roadmap',
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
  });

  isochrone.init();

  /**
   * Districts layer
   * - an outline of all postcodes
   */
  httpGetAsync(`/assets/Districts.json`, (res) => {
    const districts = JSON.parse(res);
    const districtsLayer = new google.maps.Data();
    districtsLayer.setStyle(() => ({
      fillColor: '#ffffff',
      fillOpacity: 0.1,
      strokeWeight: 1,
    }));
    districtsLayer.addGeoJson(districts);
    districtsLayer.setMap(map);
  });

  // garage marker
  new google.maps.Marker({
    position: { lat, lng },
    icon: {
      url: `${window.location.origin}/assets/images/map_marker.png`,
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(17.5, 17.5),
    },
    map,
  });

  // default path lat, lng
  // const polygonPath = [
  //   [ -2, -2 ],
  //   [ 2, -2 ],
  //   [ 2, 4 ],
  //   [ -2, 4 ],
  // ];
  let polygonPath = [
    [lng - 0.0125, lat - 0.0125],
    [lng - 0.0125, lat + 0.0125],
    [lng + 0.025, lat + 0.0125],
    [lng + 0.025, lat - 0.0125],
  ];

  if (geoJson) polygonPath = geoJson.geometry.coordinates[0];

  // const pointsCoordsArr = [
  //   { lat: -2, lng: -2 },
  //   { lat: 2, lng: -2 },
  //   { lat: 2, lng: 4 },
  //   { lat: -2, lng: 4 }
  // ];
  // convert geometry geoJson to points
  // GeoJSON specifies coordinates as [longitude,latitude].
  const pointsCoordsArr = polygonPath.map(coordsArrToLatLngArrObjects);

  polygon = new google.maps.Polygon({
    paths: pointsCoordsArr,
    strokeColor: '#FF5858',
    strokeOpacity: 1,
    strokeWeight: 2,
    fillColor: '#FF5858',
    fillOpacity: 0.7,
    editable: false,
    zIndex: 2,
  });

  polygon.setMap(map);

  // Events
  // google.maps.event.addListener(polygon.getPath(), 'set_at', () =>
  //   console.log('clicked set', polygon.getPath())
  // );
  // google.maps.event.addListener(polygon.getPath(), 'insert_at', () =>
  //   console.log('clicked insert', polygon.getPath())
  // );
  // google.maps.event.addListener(polygon.getPath(), 'remove_at', () =>
  //   console.log('clicked remove', polygon.getPath())
  // );

  /**
   * Draw nearby shapes of other garages
   */
  otherGaragesCoverage.map((garage) => {
    const { geoJson } = garage;
    const otherPolygonPath = geoJson.geometry.coordinates[0];
    const otherPointsCoordsArr = otherPolygonPath.map(coordsArrToLatLngArrObjects);

    const otherPolygon = new google.maps.Polygon({
      paths: otherPointsCoordsArr,
      strokeColor: '#1082EA',
      strokeOpacity: 0.4,
      strokeWeight: 2,
      fillColor: '#1082EA',
      fillOpacity: 0.2,
      editable: false,
      zIndex: 1,
    });

    otherPolygon.setMap(map);
  });

  deleteMenu = new DeleteMenu();

  google.maps.event.addListener(polygon, 'rightclick', function (e) {
    // Check if click was on a vertex control point
    if (e.vertex == undefined) {
      return;
    }
    if (polygon.getPath().getLength() > 3) deleteMenu.open(map, polygon.getPath(), e.vertex);
  });
};

export default function GoogleMap({ garage, updateCoverage, otherGaragesCoverage, cancelCoverageEdits }) {
  const [isEditable, setEditable] = useState(false);
  const { garageId, segment } = garage;
  const [isochroneIsLoading, setIsochroneLoading] = useState(false);
  const [drivingTime, setDrivingTime] = useState(20);
  const [transportMethod, setTransportMethod] = useState('driving');

  // componentDidMount
  useEffect(() => {
    initMap(garage, otherGaragesCoverage);
    isochrone.load({ debug: true, callback: onIsochroneComputation });
  }, [garage, otherGaragesCoverage]);

  // componentWillUdpate
  useEffect(() => {
    polygon.setEditable(isEditable);
    // polygon.setDraggable(isEditable);
  }, [isEditable]);

  const saveCoverage = useCallback(
    (garageId, segment) => {
      setEditable(false);

      // convert polygon to geoJson
      const polygonArr = polygon.getPath().getArray();
      const geoJson = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [],
        },
      };
      const pointsLatLngCoords = [];
      for (let i = 0; i < polygonArr.length; i++) {
        // GeoJSON specifies coordinates as [longitude,latitude].
        pointsLatLngCoords.push([polygonArr[i].lng(), polygonArr[i].lat()]);
      }
      geoJson.geometry.coordinates.push(pointsLatLngCoords);

      const data = {
        ownerType: 'garage',
        ownerId: garageId,
        geoJson,
        segment,
      };

      updateCoverage(data);
    },
    [isEditable]
  );

  const computeTravelReach = useCallback((parameters) => {
    setIsochroneLoading(true);
    isochrone.compute({
      lat: center.lat,
      lng: center.lng,
      cycles: 8,
      slices: 25,
      type: 'duration',
      value: drivingTime * 60, // seconds
      mode: transportMethod,
      departureTime: moment()
        .set({
          // rush hour of work day
          day: 1,
          hour: 8,
          minute: 30,
          second: 0,
        })
        .toDate(),
      callback(status, points) {
        console.log('isochrone', status, points);
        setIsochroneLoading(false);
        if (status === 'OK' && polygon) {
          // console.log('redrawing map');
          polygon.setPath(points);
          // google.maps.event.trigger(map, 'resize');
        }
      },
    });
  });

  const onIsochroneComputation = function (arr) {
    // console.log('onIsochroneComputation ', arguments);
  };

  return (
    <DivStyled>
      <GmapDivStyled id="map" />
      <div className="buttons-wrapper">
        {!isEditable && (
          <Button type="primary" onClick={() => setEditable(true)}>
            Edit coverage
          </Button>
        )}
        {isEditable && (
          <>
            <div style={{ float: 'left' }}>
              <Button type="primary" loading={isochroneIsLoading} onClick={() => computeTravelReach()}>
                Compute travel reach
              </Button>
              <span> for </span>
              <Select
                defaultValue={20}
                style={{ width: 100 }}
                onChange={(value) => setDrivingTime(parseInt(value))}
              >
                <Option value={5}>5 minutes</Option>
                <Option value={10}>10 minutes</Option>
                <Option value={15}>15 minutes</Option>
                <Option value={20}>20 minutes</Option>
                <Option value={25}>25 minutes</Option>
                <Option value={30}>30 minutes</Option>
              </Select>
              <Select
                defaultValue="driving"
                style={{ width: 100 }}
                onChange={(value) => setTransportMethod(value)}
              >
                <Option value="driving">driving</Option>
                <Option value="walking">walking</Option>
                <Option value="bicycling">cycling</Option>
                <Option value="transit">transit</Option>
              </Select>
              <span> time (rush hour)</span>
            </div>
            <Button type="primary" onClick={() => saveCoverage(garageId, segment)}>
              Save coverage
            </Button>
            <Button
              type="default"
              onClick={() => {
                setEditable(false);
                cancelCoverageEdits();
              }}
            >
              Cancel
            </Button>
          </>
        )}
      </div>
    </DivStyled>
  );
}

GoogleMap.propTypes = {
  garage: PropTypes.object,
  otherGaragesCoverage: PropTypes.array,
  updateCoverage: PropTypes.func,
  cancelCoverageEdits: PropTypes.func,
};

GoogleMap.defaultProps = {
  garage: {},
  otherGaragesCoverage: [],
  updateCoverage: () => {},
  cancelCoverageEdits: () => {},
};

/**
 * A menu that lets a user delete a selected vertex of a path.
 * @constructor
 */
function DeleteMenu() {
  this.div_ = document.createElement('div');
  this.div_.className = 'delete-menu';
  this.div_.innerHTML = 'Delete';

  const menu = this;
  google.maps.event.addDomListener(this.div_, 'click', function () {
    menu.removeVertex();
  });
}
DeleteMenu.prototype = new google.maps.OverlayView();

DeleteMenu.prototype.onAdd = function () {
  const deleteMenu = this;
  const map = this.getMap();
  this.getPanes().floatPane.appendChild(this.div_);

  // mousedown anywhere on the map except on the menu div will close the menu.
  this.divListener_ = google.maps.event.addDomListener(
    map.getDiv(),
    'mousedown',
    function (e) {
      if (e.target != deleteMenu.div_) {
        deleteMenu.close();
      }
    },
    true
  );
};

DeleteMenu.prototype.onRemove = function () {
  google.maps.event.removeListener(this.divListener_);
  this.div_.parentNode.removeChild(this.div_);

  // clean up
  this.set('position');
  this.set('path');
  this.set('vertex');
};

DeleteMenu.prototype.close = function () {
  this.setMap(null);
};

DeleteMenu.prototype.draw = function () {
  const position = this.get('position');
  const projection = this.getProjection();

  if (!position || !projection) {
    return;
  }

  const point = projection.fromLatLngToDivPixel(position);
  this.div_.style.top = `${point.y}px`;
  this.div_.style.left = `${point.x}px`;
};

/**
 * Opens the menu at a vertex of a given path.
 */
DeleteMenu.prototype.open = function (map, path, vertex) {
  this.set('position', path.getAt(vertex));
  this.set('path', path);
  this.set('vertex', vertex);
  this.setMap(map);
  this.draw();
};

/**
 * Deletes the vertex from the path.
 */
DeleteMenu.prototype.removeVertex = function () {
  const path = this.get('path');
  const vertex = this.get('vertex');

  if (!path || vertex == undefined) {
    this.close();
    return;
  }

  path.removeAt(vertex);
  this.close();
};
